import { isLocale, DEFAULT_LOCALE } from '@/i18n/locales';
import { useCurrency } from '@/currency/store';
import { currencyIconSource } from '@/currency/iconSource';
import { D, fxConfig, resolveColor } from './config';
import { getLayer, rectCenter } from './layer';
import { sound } from './sound';
import { TOKEN_CSS, tokenMarkup } from '@/art/token';
import { lanternMarkup } from './festive';

let coinSeq = 0;

/* Coins, count-up, pop, flash, shake — port of design/lobby/fx.js (DOM + Web Animations API). */

export interface Point {
  x: number;
  y: number;
}
type Anchor = Element | Point | null | undefined;

function toPoint(a: Anchor, toTarget = false): { x: number; y: number; w: number; h: number } | null {
  if (!a) return null;
  if ('getBoundingClientRect' in a) {
    const { r } = rectCenter(a);
    // coins land on the coin icon at the left of a balance chip
    const x = toTarget ? r.left + Math.min(24, r.width / 2) : r.left + r.width / 2;
    return { x, y: r.top + r.height / 2, w: r.width, h: r.height };
  }
  return { x: a.x, y: a.y, w: 80, h: 60 };
}

/** Scale pulse (button press / landing). */
export function pop(el: Element | null | undefined, scale = 1.14): void {
  if (!el || typeof el.animate !== 'function') return;
  el.animate([{ scale: '1' }, { scale: String(scale) }, { scale: '1' }], {
    duration: D(380),
    easing: 'cubic-bezier(.2,.9,.3,1.3)',
  });
}

export interface CoinFlyOptions {
  /** ring colour; default --orange (the coin of the header) */
  color?: string;
  /** play a coin plink per landed coin (default true) */
  sound?: boolean;
  signal?: AbortSignal;
}

/**
 * Coins fly from `from` to `to` (elements or viewport points). Resolves when the last coin lands.
 * Reduced motion → a single quick coin.
 */
export function coinFly(from: Anchor, to: Anchor, n = 10, o: CoinFlyOptions = {}): Promise<void> {
  const a = toPoint(from);
  const b = toPoint(to, true);
  if (!a || !b || o.signal?.aborted) return Promise.resolve();
  const L = getLayer();
  const count = fxConfig.reduced ? 1 : Math.max(1, n);
  const tint = o.color ? `--orange:${resolveColor(o.color, '--orange')};` : '';
  const target = to && 'getBoundingClientRect' in to ? to : null;
  return new Promise<void>((res) => {
    let done = 0;
    const coins: { el: HTMLDivElement; anim: Animation }[] = [];
    const finish = () => {
      for (const c of coins) {
        c.anim.cancel();
        c.el.remove();
      }
      res();
    };
    o.signal?.addEventListener('abort', finish, { once: true });
    for (let i = 0; i < count; i++) {
      const c = document.createElement('div');
      c.dataset.fxCoin = '';
      if (fxConfig.festive) c.dataset.fxLantern = '';
      c.style.cssText = `position:absolute;left:0;top:0;width:18px;height:18px;margin:-9px 0 0 -9px;${tint}`;
      // the token art (art/token.ts), or a mini lantern during a holiday; ids unique per flying coin
      c.innerHTML = fxConfig.festive
        ? `<svg viewBox="0 0 128 128" width="18" height="18" aria-hidden="true">${lanternMarkup(`fxl${++coinSeq}`)}</svg>`
        : `<svg viewBox="0 0 128 128" width="18" height="18" aria-hidden="true">${tokenMarkup(TOKEN_CSS, `fxc${++coinSeq}`, 64, 64, 1, true)}</svg>`;
      const state = useCurrency.getState();
      const lang = document.documentElement.lang;
      const currency = state.byLocale[isLocale(lang) ? lang : DEFAULT_LOCALE] ?? state.active;
      if (currency.enabled && currency.icon !== 'leyo') {
        const icon = document.createElement('img');
        icon.src = currencyIconSource(currency);
        icon.width = icon.height = 18;
        icon.alt = '';
        icon.style.objectFit = 'contain';
        c.replaceChildren(icon);
        delete c.dataset.fxLantern;
      }
      L.appendChild(c);
      const jx = (Math.random() - 0.5) * Math.min(160, a.w * 0.6);
      const jy = (Math.random() - 0.5) * Math.min(100, a.h * 0.5);
      const mx = (a.x + b.x) / 2 + jx * 0.5;
      const my = Math.min(a.y, b.y) - 60 - Math.random() * 80;
      const anim = c.animate(
        [
          { transform: `translate(${a.x}px,${a.y}px) scale(.3)`, opacity: 0 },
          { transform: `translate(${a.x + jx}px,${a.y + jy}px) scale(1.1)`, opacity: 1, offset: 0.18 },
          { transform: `translate(${mx}px,${my}px) scale(1)`, offset: 0.55 },
          { transform: `translate(${b.x}px,${b.y}px) scale(.5)`, opacity: 0.8 },
        ],
        { duration: D(850) + i * 30, delay: i * 45, easing: 'cubic-bezier(.45,0,.55,1)', fill: 'forwards' },
      );
      coins.push({ el: c, anim });
      anim.onfinish = () => {
        c.remove();
        if (o.sound !== false) sound.play('coin');
        pop(target, 1.06);
        if (++done === count) res();
      };
    }
  });
}

export interface CountUpOptions {
  prefix?: string;
  decimals?: number;
  /** number formatter (defaults to en-US grouping); pass formatTokens bound to locale */
  format?: (n: number) => string;
  /** coin ticks while counting big amounts */
  ticks?: boolean;
  signal?: AbortSignal;
}

/**
 * Animates el.textContent from → to (ease-out cubic). Reduced motion or abort → jumps to the end.
 */
export function countUp(el: Element | null | undefined, from: number, to: number, ms = 600, o: CountUpOptions = {}): Promise<void> {
  if (!el) return Promise.resolve();
  const dec = o.decimals ?? 0;
  const fmt =
    o.format ??
    ((n: number) => n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
  const write = (n: number) => {
    el.textContent = (o.prefix ?? '') + fmt(n);
  };
  if (fxConfig.reduced || o.signal?.aborted || ms <= 0) {
    write(to);
    return Promise.resolve();
  }
  const dur = D(ms);
  return new Promise<void>((res) => {
    const t0 = performance.now();
    let last = 0;
    let stopped = false;
    const end = () => {
      if (stopped) return;
      stopped = true;
      write(to);
      res();
    };
    o.signal?.addEventListener('abort', end, { once: true });
    const f = (now: number) => {
      if (stopped) return;
      // rAF timestamps are the frame start and may precede t0: clamp, or the first frame shows e.g. "−1"
      const k = Math.min(1, Math.max(0, (now - t0) / dur));
      const e = 1 - Math.pow(1 - k, 3);
      const v = from + (to - from) * e;
      write(dec ? v : Math.round(v));
      if (o.ticks !== false && now - last > 80 && k < 1 && to - from > 300) {
        last = now;
        sound.play('coin');
      }
      if (k < 1) requestAnimationFrame(f);
      else end();
    };
    requestAnimationFrame(f);
  });
}

/**
 * Colour flash over an element (or the whole screen). Default red (crash). Skipped under reduced motion.
 * color: "var(--red)" / "--teal" / literal.
 */
export function flash(color = 'var(--red)', el?: Element | null, ms = 520): void {
  if (fxConfig.reduced) return;
  const f = document.createElement('div');
  f.dataset.fxFlash = '';
  const r = el ? el.getBoundingClientRect() : { left: 0, top: 0, width: innerWidth, height: innerHeight };
  f.style.cssText = `position:absolute;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;background:${resolveColor(color, '--red')};opacity:0;`;
  getLayer().appendChild(f);
  const a = f.animate([{ opacity: 0.4 }, { opacity: 0 }], { duration: ms });
  a.onfinish = () => f.remove();
}

/** 320ms shake. Skipped under reduced motion. */
export function shake(el: Element | null | undefined): void {
  if (!el || fxConfig.reduced || typeof el.animate !== 'function') return;
  el.animate(
    [
      { translate: '0 0' },
      { translate: '-9px 3px' },
      { translate: '8px -3px' },
      { translate: '-4px 2px' },
      { translate: '0 0' },
    ],
    { duration: 320 },
  );
}

/** Small ring particles bursting from a point inside `parent` (cascade pops). */
export function burst(parent: HTMLElement | null | undefined, x: number, y: number, n = 7, color = 'var(--blue)'): void {
  if (!parent || fxConfig.reduced) return;
  const col = resolveColor(color, '--blue');
  for (let i = 0; i < n; i++) {
    const p = document.createElement('div');
    const a = Math.random() * Math.PI * 2;
    const d = 20 + Math.random() * 40;
    const s = 4 + Math.random() * 6;
    p.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${s}px;height:${s}px;border-radius:50%;border:1.5px solid ${col};pointer-events:none;z-index:5;`;
    parent.appendChild(p);
    const an = p.animate(
      [
        { transform: 'translate(-50%,-50%) scale(.4)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${Math.cos(a) * d}px), calc(-50% + ${Math.sin(a) * d - 20}px)) scale(1)`,
          opacity: 0,
        },
      ],
      { duration: 500 + Math.random() * 300, easing: 'cubic-bezier(.2,.7,.3,1)' },
    );
    an.onfinish = () => p.remove();
  }
}
